import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { JWTPayload, decodeJwt } from "jose";
import { ALL_ROUTES, AppRoutes, PRIVATE_ROUTES } from "types";
import { StorageKey, Storage } from "lib/storage";

type JWT = JWTPayload & {
  registered: boolean;
  email: string;
  contributor?: string;
  guardian_user_id?: string;
};

export interface AuthContextInterface {
  token: JWT | null | undefined;
  setToken: (token: string | null | undefined) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextInterface>(
  {} as AuthContextInterface
);

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [token, _setToken] = useState<JWT | null | undefined>();
  const [init, setInit] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const path = useMemo(
    () => location.pathname as AppRoutes,
    [location.pathname]
  );
  const navigate = useNavigate();

  const setToken = useCallback((token: string | null | undefined) => {
    try {
      if (typeof token !== "string" || token.length <= 0) {
        throw new Error("wrong token");
      }

      _setToken(decodeJwt(token) as JWT);
    } catch (err) {
      _setToken(null);
    }
  }, []);

  useEffect(() => {
    _setToken(undefined);
    setInit(false);
  }, [location.pathname]);

  // ANCHOR: init token from localStorage
  useEffect(() => {
    if (token !== undefined) {
      return;
    }

    try {
      setToken(Storage.get(StorageKey.TOKEN));
    } catch (err) {
      _setToken(null);
    }
  }, [setToken, token]);

  useEffect(() => {
    // ANCHOR: redirect if path is not specified
    if (!ALL_ROUTES.includes(path)) {
      navigate(AppRoutes.home);
      return;
    }

    if (token === undefined) {
      return;
    }

    if (token === null && PRIVATE_ROUTES.includes(path)) {
      navigate({
        pathname: AppRoutes.loginOrSignup,
        search: searchParams.toString(),
      });
      return;
    }

    if (
      token &&
      (token?.exp || 0) < Date.now() / 1000 &&
      PRIVATE_ROUTES.includes(path)
    ) {
      navigate(AppRoutes.logout);
      return;
    }

    if (token && !token.registered && AppRoutes.signup !== path) {
      navigate({
        pathname: AppRoutes.signup,
        search: searchParams.toString(),
      });
      return;
    }

    // Check if user has validated campaign passphrase (has contributor in JWT)
    const hasContributor = token && token.contributor;

    // If registered but no contributor, and trying to access private routes (except welcome), redirect to welcome
    if (
      token &&
      token.registered &&
      !hasContributor &&
      PRIVATE_ROUTES.includes(path) &&
      path !== AppRoutes.welcome
    ) {
      // Keep query params (e.g. ?passphrase from QR deep links) so the
      // welcome page can prefill the campaign passphrase.
      navigate({
        pathname: AppRoutes.welcome,
        search: searchParams.toString(),
      });
      return;
    }

    if (
      token &&
      token.registered &&
      [AppRoutes.login, AppRoutes.loginOrSignup, AppRoutes.signup].includes(
        path
      )
    ) {
      // If they have contributor, go to home; otherwise go to welcome
      // (keeping query params so ?passphrase reaches the welcome page)
      navigate(
        hasContributor
          ? AppRoutes.home
          : { pathname: AppRoutes.welcome, search: searchParams.toString() }
      );
      return;
    }

    setInit(true);
  }, [navigate, path, searchParams, token]);

  const logout = useCallback(() => {
    navigate(AppRoutes.logout);
  }, [navigate]);

  if (!init) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ token, setToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
